<template>
  <wizzard-container :infos="infos">
    <div class="title">{{ $t('Business Form Pricing') }}</div>
    <p class="caption">{{ $t('Business Form Pricing Caption') }}</p>
    <v-select
      v-model="form.priceLevel"
      :items="priceLevelOptions"
      item-text="text"
      item-value="value"
      :label="$t('Business Form Price Level')"
      outlined
      dense
      :error-messages="firstError('form.priceLevel')"
    />
    <v-select
      v-model="form.priceRangeCurrency"
      :items="currencyOptions"
      item-text="text"
      item-value="value"
      :label="$t('Business Form Price Range Currency')"
      outlined
      dense
      :error-messages="firstError('form.priceRangeCurrency')"
    />
    <v-text-field
      v-model="form.priceRangeStart"
      type="number"
      :label="$t('Business Form Price Range Start')"
      outlined
      dense
      :error-messages="firstError('form.priceRangeStart')"
    />
    <v-text-field
      v-model="form.priceRangeEnd"
      type="number"
      :label="$t('Business Form Price Range End')"
      outlined
      dense
      :error-messages="firstError('form.priceRangeEnd')"
    />
    <form-action-buttons
      status="listed"
      @submit="submit"
    />
  </wizzard-container>
</template>

<script>
import axios from 'axios';
import { validationMixin } from 'vuelidate';
import { maxLength, maxValue } from 'vuelidate/lib/validators';
import FormValidationMixin from '@/lib/FormValidationMixin';
import FormActionButtons from '@/views/ads/forms/FormActionButtons.vue';
import WizzardContainer from '@/views/ads/forms/WizzardContainer.vue';

export default {
  mixins: [validationMixin, FormValidationMixin],
  components: {
    FormActionButtons,
    WizzardContainer,
  },
  props: {
    businessId: { type: String, required: true },
    business: { type: Object, default: null },
    currencies: { type: Array, required: true },
  },
  validations() {
    return {
      form: {
        priceRangeStart: {
          maxLength: maxLength(30),
          maxValue: maxValue(this.form.priceRangeEnd),
        },
        priceRangeEnd: {
          maxLength: maxLength(30),
        },
        priceRangeCurrency: {
          maxLength: maxLength(10),
        },
      },
    };
  },
  data() {
    return {
      form: {
        priceLevel: null,
        priceRangeStart: null,
        priceRangeEnd: null,
        priceRangeCurrency: null,
      },
    };
  },
  watch: {
    business: {
      immediate: true,
      handler() {
        this.setFormData();
      },
    },
  },
  computed: {
    priceLevelOptions() {
      return [
        { text: '', value: null },
        { text: this.$t('Business Form Free'), value: 'PRICE_LEVEL_FREE' },
        { text: '$', value: 'PRICE_LEVEL_INEXPENSIVE' },
        { text: '$$', value: 'PRICE_LEVEL_MODERATE' },
        { text: '$$$', value: 'PRICE_LEVEL_EXPENSIVE' },
        { text: '$$$$', value: 'PRICE_LEVEL_VERY_EXPENSIVE' },
      ];
    },
    currencyOptions() {
      const options = [
        { text: '', value: null },
        ...this.currencies.map((currency) => ({
          text: currency.code,
          value: currency.code,
        })),
      ];

      if (
        this.form.priceRangeCurrency
        && !options.some((option) => option.value === this.form.priceRangeCurrency)
      ) {
        options.push({
          text: this.form.priceRangeCurrency,
          value: this.form.priceRangeCurrency,
        });
      }

      return options;
    },
    infos() {
      return [
        {
          icon: 'green-energy',
          title: 'Business Form Pricing InfoTitle',
          description: 'Business Form Pricing InfoDescription',
        },
      ];
    },
  },
  methods: {
    setFormData() {
      if (!this.business) {
        return;
      }

      this.form = {
        priceLevel: this.business.priceLevel,
        priceRangeStart: this.business.priceRangeStart,
        priceRangeEnd: this.business.priceRangeEnd,
        priceRangeCurrency: this.business.priceRangeCurrency,
      };
    },
    submit() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        this.scrollToFirstError();
        return;
      }
      this.$store.dispatch('loading', true);
      axios
        .post(`panel/ads/validation/business/pricing/${this.businessId}`, this.form)
        .then(() => {
          this.$store.dispatch('clearErrors');
          this.$store.dispatch('message', this.$t('Saved successfully'));
          this.$emit('success');
        })
        .catch((error) => {
          const errors = error.response && error.response.data && error.response.data.errors
            ? error.response.data.errors
            : {};
          this.$store.dispatch('setErrors', errors);
          this.scrollToFirstError();
        })
        .finally(() => {
          this.$store.dispatch('loading', false);
        });
    },
  },
};
</script>
